'use client';

import { useQuery } from '@tanstack/react-query';
import {
  fetchContentModerationFindingDetail,
  provenanceKeys,
  type ContentModerationFindingDetail,
  type DetectionType,
} from '@/shared/cors-provenance-api';
import { useProvenanceSession } from './useProvenanceSession';
import { useContentModerationCase } from './useContentModerationFolderName';
import { toDetectionType } from './useContentModerationImageDescription';

/**
 * One finding's detector JSON and image description.
 *
 * The list query stays on DynamoDB. This runs when a row is opened, and the
 * folder segment is resolved the same way as {@link useContentModerationFindings}.
 */
export function useContentModerationFindingDetail(
  caseId: string | null | undefined,
  finding: { fileMd5: string; detectionType: string } | null | undefined,
  opts: { enabled?: boolean } = {},
) {
  const { cellId, accessToken, enabled: sessionEnabled } = useProvenanceSession();
  const { folderName, isResolving: isCaseResolving } = useContentModerationCase(caseId);
  const detectionType = toDetectionType(finding?.detectionType) ?? ('CSAM' as DetectionType);
  const fileMd5 = finding?.fileMd5 ?? null;
  const enabled = (opts.enabled ?? true) && sessionEnabled && Boolean(folderName) && Boolean(fileMd5);

  const query = useQuery<ContentModerationFindingDetail, Error>({
    queryKey: provenanceKeys.contentModerationFindingDetail(
      cellId,
      folderName,
      detectionType,
      fileMd5,
    ),
    queryFn: ({ signal }) =>
      fetchContentModerationFindingDetail({
        cellId: cellId as string,
        accessToken: accessToken as string,
        caseId: folderName as string,
        detectionType,
        fileMd5: fileMd5 as string,
        signal,
      }),
    enabled,
    staleTime: 30 * 1000,
  });

  return Object.assign(query, { isCaseResolving });
}
